/** One renderer per board column kind (text, number, date, duration, status, steps, badges, link, media, progress).
 * Cells come from @board_column plugins: {value, text, state, href, media_id, tone, items}. Text is always in the
 * DOM (never colour-only, never hover-only). */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { mediaUrl, type Schemas } from "../../api/client";
import { StatusPill } from "../../components/StatusPill";
import { fmtDuration, fmtNum, fmtPct, fmtWhen, isoUtc } from "../lib/time";

export type Cell = Schemas["Cell"];
export const CELL_KINDS = ["text", "number", "date", "duration", "status", "steps", "badges", "link", "media", "progress"] as const;

const STATE_LABEL: Record<string, string> = { needs_human: "needs human" };

function str(x: unknown): string | undefined {
  if (x == null) return undefined;
  return typeof x === "string" ? x : typeof x === "number" || typeof x === "boolean" ? String(x) : undefined;
}

function num(x: unknown): number | undefined {
  return typeof x === "number" && Number.isFinite(x) ? x : undefined;
}

export function AppLink({ href, children, label }: { href: string; children: ReactNode; label?: string }) {
  if (href.startsWith("/")) return <Link to={href} aria-label={label}>{children}</Link>;
  return (
    <a href={href} target="_blank" rel="noreferrer" aria-label={label}>
      {children}
    </a>
  );
}

const dash = <span className="ws7-empty">–</span>;

function toneClass(c: Cell): string | undefined {
  return c.tone ? `ws7-tone-${c.tone}` : undefined;
}

function TextCell({ c }: { c: Cell }) {
  const t = c.text ?? str(c.value);
  if (!t) return dash;
  const body = <span className={toneClass(c)}>{t}</span>;
  return c.href ? <AppLink href={c.href}>{body}</AppLink> : body;
}

function NumberCell({ c }: { c: Cell }) {
  const n = num(c.value);
  const t = c.text || (n !== undefined ? fmtNum(n) : undefined);
  return t ? <span className={toneClass(c)}>{t}</span> : dash;
}

function DateCell({ c }: { c: Cell }) {
  const n = num(c.value);
  if (n !== undefined) {
    // unix seconds → Budapest local time
    return <time dateTime={isoUtc(n)}>{fmtWhen(n)}</time>;
  }
  const t = c.text ?? str(c.value);
  return t ? <span>{t}</span> : dash;
}

function DurationCell({ c }: { c: Cell }) {
  const n = num(c.value);
  if (n !== undefined) return <span>{fmtDuration(n)}</span>;
  return c.text ? <span>{c.text}</span> : dash;
}

function StatusCell({ c }: { c: Cell }) {
  if (c.state) {
    const stateText = STATE_LABEL[c.state] ?? c.state;
    const label = c.text && c.text !== c.state && c.text !== stateText ? c.text : undefined;
    return <StatusPill state={c.state} label={label} />;
  }
  const t = c.text ?? str(c.value);
  return t ? <span className={`pill ${toneClass(c) ?? ""}`}>{t}</span> : dash;
}

export function stepLabel(item: Record<string, unknown>): string {
  const step = str(item.step) ?? str(item.name) ?? "step";
  const pos = num(item.queue_position);
  return pos !== undefined && str(item.state) === "queued" ? `${step} #${pos}` : step;
}

function StepsCell({ c }: { c: Cell }) {
  const items = c.items ?? [];
  if (items.length === 0) return c.text && c.text !== "–" ? <span>{c.text}</span> : dash;
  return (
    <ul className="ws7-steps" aria-label={c.text ?? "pipeline steps"}>
      {items.map((it, i) => {
        const state = str(it.state) ?? "pending";
        const msg = str(it.message);
        const showMsg = msg && ["failed", "needs_human", "blocked", "stale"].includes(state);
        return (
          <li key={`${stepLabel(it)}-${i}`}>
            <StatusPill state={state} label={stepLabel(it)} title={msg} />
            {showMsg && <span className="ws7-step-msg">{msg}</span>}
          </li>
        );
      })}
    </ul>
  );
}

function BadgesCell({ c }: { c: Cell }) {
  const items = c.items ?? [];
  if (items.length === 0) return c.text ? <span>{c.text}</span> : dash;
  return (
    <ul className="ws7-chips">
      {items.map((it, i) => {
        const name = str(it.name) ?? str(it.label) ?? str(it.text) ?? str(it.value) ?? "?";
        const feat = it.role === "feat" || it.feat === true;
        return (
          <li key={`${name}-${i}`} className={`chip${feat ? " ws7-chip-feat" : ""}`} data-feat={feat ? "1" : undefined}>
            {feat ? `${name} (feat)` : name}
          </li>
        );
      })}
    </ul>
  );
}

function LinkCell({ c }: { c: Cell }) {
  const href = c.href ?? (typeof c.value === "string" && /^(https?:\/\/|\/)/.test(c.value) ? c.value : undefined);
  const t = c.text ?? str(c.value) ?? href;
  if (!href) return t ? <span>{t}</span> : dash;
  return <AppLink href={href}>{t}</AppLink>;
}

function MediaCell({ c }: { c: Cell }) {
  const alt = c.text ?? "thumbnail";
  const src = c.media_id != null ? mediaUrl(c.media_id) : typeof c.value === "string" && /^https?:\/\//.test(c.value) ? c.value : undefined;
  if (!src) return c.text ? <span>{c.text}</span> : dash;
  const img = <img className="ws7-thumb" src={src} alt={alt} loading="lazy" width={96} />;
  const href = c.href ?? src;
  return <AppLink href={href} label={`Open ${alt}`}>{img}</AppLink>;
}

function ProgressCell({ c }: { c: Cell }) {
  const v = num(c.value);
  if (v === undefined) return c.text ? <span>{c.text}</span> : dash;
  const clamped = Math.max(0, Math.min(1, v));
  const pct = fmtPct(clamped);
  return (
    <span className="ws7-progress">
      <progress max={1} value={clamped} aria-label={`progress ${pct}`} />
      <span>{c.text ?? pct}</span>
    </span>
  );
}

const RENDERERS: Record<string, (p: { c: Cell }) => ReactNode> = {
  text: TextCell,
  number: NumberCell,
  date: DateCell,
  duration: DurationCell,
  status: StatusCell,
  steps: StepsCell,
  badges: BadgesCell,
  link: LinkCell,
  media: MediaCell,
  progress: ProgressCell,
};

export function CellView({ kind, cell }: { kind: string; cell: Cell | undefined }) {
  const R = RENDERERS[kind] ?? TextCell;
  return <R c={cell ?? {}} />;
}

/** Plain-text twin of a cell (used for accessible names and tests). */
export function cellText(kind: string, c: Cell | undefined): string {
  if (!c) return "";
  const n = num(c.value);
  switch (kind) {
    case "date":
      return n !== undefined ? fmtWhen(n) : (c.text ?? str(c.value) ?? "");
    case "duration":
      return n !== undefined ? fmtDuration(n) : (c.text ?? "");
    case "number":
      return c.text || (n !== undefined ? fmtNum(n) : "");
    case "progress":
      return n !== undefined ? fmtPct(n) : (c.text ?? "");
    default:
      return c.text ?? str(c.value) ?? "";
  }
}

